import React, { useEffect, useMemo, useState } from 'react';
import MagicForm from '../../../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../../../Enums/MagicField.enum';
import { User } from '../../../../../graphql/graphql';
import { useEffectAsync } from '../../../../../helpers';
import Loading from '../../../../../Components/Loading';
import usePatientAddressContainer from '../../../Hooks/usePatientAddressContainer';
import useCapitation from '../../../../Workplace/Hooks/useCapitation';
import { UserEditedMeta } from '../../../../../Components/PatientProfile/PatientProfileParts/PatientProfileInformation';
import { getUsersVzpSupplementaryInsuranceType } from '../../../../User/Services/userMeta.service';

interface Props {
	onSubmit: (values: any) => void;
	user: User | UserEditedMeta;
	workplaceId: string;
}

const DEFAULT_FORM_SCHEME: ItemScheme[] = [
	{
		type: MagicField.TEXT_FIELD,
		key: 'salutationBefore',
		title: 'component.patientProfileEdit.salutationBefore',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'firstName',
		title: 'component.patientProfileEdit.firstName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'lastName',
		title: 'component.patientProfileEdit.lastName',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'salutationAfter',
		title: 'component.patientProfileEdit.salutationAfter',
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'birthNumber',
		title: 'component.patientProfileEdit.birthNumber',
		settings: {
			requiredRules: [
				{
					formControlKey: 'citizenship',
					rule: 'notEqual',
					value: '-1',
					compareKey: 'secondaryValue',
				},
			],
		},
	},
	{
		type: MagicField.CONDITION,
		subType: 'view',
		value: 'citizenship',
		settings: {
			rules: [
				{
					formControlKey: 'citizenship',
					rule: 'equal',
					value: '-1',
				},
			],
		},
		children: [
			{
				type: MagicField.TEXT_FIELD,
				key: 'insuranceNumber',
				title: 'component.patientProfileEdit.insuranceNumber',
				settings: {
					required: false,
				},
			},
		],
	},
	{
		type: MagicField.DATEPICKER,
		key: 'birthDate',
		title: 'component.patientProfileEdit.birthDate',
		settings: {
			required: true,
			rules: [
				{
					rule: 'max',
					value: 'today',
				},
			],
		},
	},
	{
		type: MagicField.EMAIL,
		key: 'email',
		title: 'component.patientProfileEdit.email',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.PHONE_NUMBER,
		key: 'phoneNumber',
		title: 'component.patientProfileEdit.phone',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.CITIZENSHIP_SELECT,
		key: 'citizenship',
		title: 'component.patientProfileEdit.citizenship',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.HEALTH_INSURANCE_COMPANY_SELECT,
		key: 'healthInsuranceCompany',
		title: 'component.patientProfileEdit.healthInsuranceCompany',
		settings: {
			required: true,
		},
	},
	{
		type: MagicField.TERM_ITEM_SELECT,
		key: 'vzpSupplementaryInsuranceType',
		title: 'base.vzpSupplementaryInsuranceType',
		settings: {
			termType: 'vzp_supplementary_insurance_type',
			labelFormat: '{value} - {name}',
		},
	},
	{
		type: MagicField.GENDER,
		key: 'gender',
		title: 'component.patientProfileEdit.gender',
	},
];

const CAPITATION_FORM_FIELDS: ItemScheme[] = [
	{
		type: MagicField.CHECKBOX,
		key: 'capitationRegistration',
		title: 'base.capitationRegistration',
	},
	{
		type: MagicField.CONDITION,
		settings: {
			rules: [
				{
					formControlKey: 'capitationRegistration',
					rule: 'equal',
					value: true,
				},
			],
		},
		children: [
			{
				type: MagicField.DATEPICKER,
				key: 'capitationRegistrationAt',
				title: 'base.capitationRegistrationAt',
				settings: {
					required: true,
				},
			},
		],
	},
];

const PatientProfileEditCzech: React.FC<Props> = ({
	onSubmit,
	user,
	workplaceId,
}) => {
	const { addAddressContainerToScheme, isLoading } =
		usePatientAddressContainer();

	const { isCapitationAllowed } = useCapitation(workplaceId);

	const formScheme = useMemo(() => {
		if (isCapitationAllowed) {
			return [...DEFAULT_FORM_SCHEME, ...CAPITATION_FORM_FIELDS];
		}
		return DEFAULT_FORM_SCHEME;
	}, [isCapitationAllowed]);

	const defaultValues: Record<string, any> = {
		salutationBefore: user.profile?.salutationBefore,
		salutationAfter: user.profile?.salutationAfter,
		firstName: user.profile?.firstName,
		lastName: user.profile?.lastName,
		birthDate: user.profile?.birthDate,
		address: user.profile?.address,
		email: user?.profile?.contactEmail ?? user?.email,
		phoneNumber: user?.phoneNumber,
		citizenship: user?.profile?.nationalityTermItem?.id,
		healthInsuranceCompany: user?.profile?.insuranceCompanyTermItem?.id,
		vzpSupplementaryInsuranceType: getUsersVzpSupplementaryInsuranceType(user),
		birthNumber: user?.profile?.birthNumber,
		insuranceNumber: user?.profile?.insuranceNumber,
		gender: user?.profile?.gender,
		capitationRegistration: !!user?.profile?.capitationRegistrationAt,
		capitationRegistrationAt: user?.profile?.capitationRegistrationAt,
	};

	const handleSubmit = (values: any) => {
		const data = {
			salutationBefore: values?.salutationBefore,
			salutationAfter: values?.salutationAfter,
			firstName: values?.firstName,
			lastName: values?.lastName,
			birthDate: values?.birthDate,
			address: values?.address,
			email: values?.email,
			phoneNumber: values?.phoneNumber,
			citizenship: values?.citizenship?.value,
			insuranceCompanyTermItemId: values?.healthInsuranceCompany?.value,
			vzpSupplementaryInsuranceTypeTermItemId:
				values?.vzpSupplementaryInsuranceType?.value,
			birthNumber: values?.birthNumber,
			gender: values?.gender?.value,
			insuranceNumber: values?.insuranceNumber,
			capitationRegistrationAt: values?.capitationRegistrationAt,
		};
		onSubmit(data);
	};

	useEffectAsync(async () => {
		if (!formScheme.some(item => item.key === 'address')) {
			await addAddressContainerToScheme(formScheme);
		}
	});

	if (isLoading) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			onSubmit={handleSubmit}
			defaultValues={defaultValues}
			showCharCounter={false}
		/>
	);
};

export default PatientProfileEditCzech;
